import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { AidaOrganizationBasic } from '../services/api';
import { useApi } from '../services/apiHooks';

import { useSearchParams, useNavigate } from 'react-router-dom';

interface OrganizationContextType {
  organizations: AidaOrganizationBasic[];
  selectedOrganization: AidaOrganizationBasic | null;
  setSelectedOrganization: (organization: AidaOrganizationBasic | null) => void;
}

export const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [organizations, setOrganizations] = useState<AidaOrganizationBasic[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<AidaOrganizationBasic | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const api = useApi();

  useEffect(() => {
    const fetchOrganizations = async () => {
      const googleIdToken = localStorage.getItem('googleIdToken');
      if (!googleIdToken) {
        return;
      }

      try {
        const jwtDecode = (await import('jwt-decode')).default;
        const decoded: any = jwtDecode(googleIdToken);
        const orgs = await api.getOrganizations(decoded.email);
        const sortedOrgs = orgs.sort((a, b) => a.name.localeCompare(b.name));
        setOrganizations(sortedOrgs);
        const orgIdFromUrl = searchParams.get('orgId');
        if (orgIdFromUrl) {
          const orgFromUrl = sortedOrgs.find(org => org.id === orgIdFromUrl);
          if (orgFromUrl) {
            setSelectedOrganization(orgFromUrl);
            return;
          }
        }
        if (sortedOrgs.length > 0) {
          setSelectedOrganization(sortedOrgs[0]);
          setSearchParams({ orgId: sortedOrgs[0].id });
        } else {
          navigate('/not-authorized');
        }
      } catch (error) {
        console.error('Failed to fetch organizations', error);
      }
    };

    fetchOrganizations();
  }, [api, searchParams, setSearchParams, navigate]);

  return (
    <OrganizationContext.Provider value={{ organizations, selectedOrganization, setSelectedOrganization }}>
      {children}
    </OrganizationContext.Provider>
  );
};
